import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';

import {ConfigService} from '@ngmedax/config';
import {RegistryService} from '@ngmedax/registry';

import {
  MasterDataLayout,
  MasterDataFieldPosition,
  GridSize,
  CustomFieldDefinition,
  StandardFieldDefinition,
  ClientGridRendererConfig
} from '../../../types';
import {
  DEFAULT_MASTER_DATA_LAYOUT,
  DEFAULT_GRID_SIZE,
  STANDARD_FIELD_DEFINITIONS,
  STANDARD_FIELD_LABEL_KEYS,
  SETTINGS_KEY_GRID_SIZE,
  SETTINGS_KEY_POSITIONS,
  SETTINGS_KEY_GRID_RENDERER,
  DEFAULT_GRID_RENDERER_CONFIG
} from '../../../constants';


/**
 * Config keys for API access.
 */
const CONFIG_KEYS = {
  SETTINGS_URI: 'apis.settings.uri',
  SESSION_AUTH_TOKEN: 'session.authToken',
  SESSION_TENANT_ID: 'session.tenantId'
};

/**
 * Service for managing master data layout configuration.
 * Provides layout loading/saving, default layout, CSS grid helpers, and custom field definitions.
 */
@Injectable()
export class MasterDataLayoutService {
  /**
   * Cached default layout to avoid creating new objects on every access.
   */
  private cachedDefaultLayout: MasterDataLayout | null = null;

  /**
   * Cached grid renderer config to avoid redundant API calls from each grid row.
   */
  private cachedGridRendererConfig: ClientGridRendererConfig | null = null;

  /**
   * Whether the grid renderer config has been loaded at least once.
   */
  private gridRendererConfigLoaded = false;

  /**
   * Injects dependencies.
   */
  public constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private registryService: RegistryService
  ) {
  }

  // =========================================================================
  // Layout Loading / Saving
  // =========================================================================

  /**
   * Loads the master data layout configuration from the settings API.
   *
   * @returns Promise resolving to layout or null if not configured.
   */
  public async loadLayout(): Promise<MasterDataLayout | null> {
    const [gridSize, positions] = await Promise.all([
      this.getSetting<GridSize>(SETTINGS_KEY_GRID_SIZE),
      this.getSetting<MasterDataFieldPosition[]>(SETTINGS_KEY_POSITIONS)
    ]);

    if (!positions || positions.length === 0) {
      return null;
    }

    return {
      gridSize: gridSize || DEFAULT_GRID_SIZE,
      positions: positions
    };
  }

  /**
   * Saves the layout configuration to the settings API.
   *
   * @param layout The layout configuration to save.
   */
  public async saveLayout(layout: MasterDataLayout): Promise<void> {
    await Promise.all([
      this.setSetting(SETTINGS_KEY_GRID_SIZE, layout.gridSize),
      this.setSetting(SETTINGS_KEY_POSITIONS, layout.positions)
    ]);
  }

  /**
   * Saves only the grid size.
   *
   * @param gridSize The grid size to save.
   */
  public async saveGridSize(gridSize: GridSize): Promise<void> {
    await this.setSetting(SETTINGS_KEY_GRID_SIZE, gridSize);
  }

  /**
   * Saves only the field positions.
   *
   * @param positions The positions to save.
   */
  public async savePositions(positions: MasterDataFieldPosition[]): Promise<void> {
    await this.setSetting(SETTINGS_KEY_POSITIONS, positions);
  }

  /**
   * Resets the layout to default (empty positions).
   */
  public async resetLayout(): Promise<void> {
    await this.setSetting(SETTINGS_KEY_POSITIONS, []);
  }

  // =========================================================================
  // Default Layout
  // =========================================================================

  /**
   * Returns the default layout matching the traditional static form appearance.
   * Only includes standard fields - custom fields are not shown when no layout is defined.
   *
   * @returns Default MasterDataLayout configuration.
   */
  public getDefaultLayout(): MasterDataLayout {
    if (this.cachedDefaultLayout) {
      return this.cachedDefaultLayout;
    }

    this.cachedDefaultLayout = {
      gridSize: {...DEFAULT_MASTER_DATA_LAYOUT.gridSize},
      positions: DEFAULT_MASTER_DATA_LAYOUT.positions.map(p => ({...p}))
    };

    return this.cachedDefaultLayout;
  }

  /**
   * Checks if a custom layout is configured.
   *
   * @param layout The layout to check.
   * @returns true if layout has positions defined.
   */
  public hasCustomLayout(layout: MasterDataLayout | null): boolean {
    return layout !== null &&
      layout.positions &&
      layout.positions.length > 0;
  }

  /**
   * Gets the effective layout (custom or default).
   *
   * @param customLayout The custom layout from API or null.
   * @returns The effective layout to use.
   */
  public getEffectiveLayout(customLayout: MasterDataLayout | null): MasterDataLayout {
    if (this.hasCustomLayout(customLayout)) {
      return customLayout;
    }
    return this.getDefaultLayout();
  }

  // =========================================================================
  // Custom Field Definitions
  // =========================================================================

  /**
   * Loads custom field definitions from the API.
   *
   * @returns Promise resolving to array of custom field definitions.
   */
  public loadCustomFieldDefinitions(): Promise<CustomFieldDefinition[]> {
    const settingsUri = this.configService.get(CONFIG_KEYS.SETTINGS_URI);

    if (!settingsUri) {
      return Promise.resolve([]);
    }

    const uri = `${settingsUri}/client-custom`;

    return new Promise((resolve) => {
      this.http.get(uri, {headers: this.getAuthHeaders()})
        .subscribe(
          (response: any) => {
            // support multiple response formats (same as SettingsClientCustomService)
            const rows = response && (response.definitions || response.customFields || response.rows) ?
              (response.definitions || response.customFields || response.rows) : [];
            resolve(rows);
          },
          (error) => {
            console.error('Failed to load custom field definitions', error);
            resolve([]);
          }
        );
    });
  }

  // =========================================================================
  // Standard Field Definitions
  // =========================================================================

  /**
   * Returns the standard field definitions.
   *
   * @returns Array of standard field definitions.
   */
  public getStandardFieldDefinitions(): StandardFieldDefinition[] {
    return STANDARD_FIELD_DEFINITIONS;
  }

  /**
   * Gets the translation label key for a standard field.
   *
   * @param fieldKey The field key.
   * @returns The translation label key or the fieldKey in uppercase as fallback.
   */
  public getStandardFieldLabelKey(fieldKey: string): string {
    return STANDARD_FIELD_LABEL_KEYS[fieldKey] || fieldKey.toUpperCase();
  }

  // =========================================================================
  // Visibility Helpers
  // =========================================================================

  /**
   * Checks if a field is visible in the given layout.
   *
   * @param layout The layout configuration.
   * @param fieldKey The field key to check.
   * @returns true if the field is in the layout.
   */
  public isFieldVisible(layout: MasterDataLayout, fieldKey: string): boolean {
    return layout.positions.some(p => p.fieldKey === fieldKey);
  }

  /**
   * Gets all visible standard field keys from the layout.
   *
   * @param layout The layout configuration.
   * @returns Array of visible standard field keys.
   */
  public getVisibleStandardFields(layout: MasterDataLayout): string[] {
    return layout.positions
      .filter(p => p.fieldType === 'standard')
      .map(p => p.fieldKey);
  }

  /**
   * Gets all visible custom field keys from the layout.
   *
   * @param layout The layout configuration.
   * @returns Array of visible custom field keys.
   */
  public getVisibleCustomFields(layout: MasterDataLayout): string[] {
    return layout.positions
      .filter(p => p.fieldType === 'custom')
      .map(p => p.fieldKey);
  }

  /**
   * Gets all searchable field positions from the layout.
   * Keeps HR elements and deprecated fields (rendered as HR in search).
   *
   * @param layout The layout configuration.
   * @returns Array of searchable field positions.
   */
  public getSearchableFields(layout: MasterDataLayout): MasterDataFieldPosition[] {
    return layout.positions;
  }

  // =========================================================================
  // CSS Grid Helpers
  // =========================================================================

  /**
   * Gets the CSS grid-template-columns value for the layout.
   *
   * @param layout The layout configuration.
   * @returns CSS grid-template-columns value.
   */
  public getGridTemplateColumns(layout: MasterDataLayout): string {
    return `repeat(${layout.gridSize.cols}, 1fr)`;
  }

  /**
   * Gets the CSS grid-column style for a field position.
   *
   * @param position The field position.
   * @returns CSS grid-column value.
   */
  public getGridColumn(position: MasterDataFieldPosition): string {
    return `${position.col + 1} / span ${position.colSpan}`;
  }

  /**
   * Gets the CSS grid-row style for a field position.
   *
   * @param position The field position.
   * @returns CSS grid-row value.
   */
  public getGridRow(position: MasterDataFieldPosition): string {
    return `${position.row + 1} / span ${position.rowSpan}`;
  }

  /**
   * Creates an array of numbers for grid iteration.
   *
   * @param n The count.
   * @returns Array of numbers from 0 to n-1.
   */
  public range(n: number): number[] {
    return Array.from({length: n}, (_, i) => i);
  }

  /**
   * Clears all cached data.
   * Useful for testing or when layout definition changes.
   */
  public clearCache(): void {
    this.cachedDefaultLayout = null;
    this.cachedGridRendererConfig = null;
    this.gridRendererConfigLoaded = false;
  }

  // =========================================================================
  // Grid Renderer Config
  // =========================================================================

  /**
   * Loads the grid renderer configuration from the settings API.
   *
   * @returns Promise resolving to config or null if not configured.
   */
  public async loadGridRendererConfig(): Promise<ClientGridRendererConfig | null> {
    return this.getSetting<ClientGridRendererConfig>(SETTINGS_KEY_GRID_RENDERER);
  }

  /**
   * Returns cached grid renderer config or loads it once from API.
   * Prevents redundant API calls when multiple grid rows render simultaneously.
   *
   * @returns Promise resolving to config or null if not configured.
   */
  public async getOrLoadGridRendererConfig(): Promise<ClientGridRendererConfig | null> {
    if (this.gridRendererConfigLoaded) {
      return this.cachedGridRendererConfig;
    }

    this.cachedGridRendererConfig = await this.loadGridRendererConfig();
    this.gridRendererConfigLoaded = true;
    return this.cachedGridRendererConfig;
  }

  /**
   * Saves the grid renderer configuration to the settings API.
   *
   * @param config The grid renderer configuration to save.
   */
  public async saveGridRendererConfig(config: ClientGridRendererConfig): Promise<void> {
    await this.setSetting(SETTINGS_KEY_GRID_RENDERER, config);

    // the patient list reads the cached config, so it shows the saved column without a page reload
    this.cachedGridRendererConfig = config;
    this.gridRendererConfigLoaded = true;
  }

  /**
   * Returns the default grid renderer configuration.
   *
   * @returns Default ClientGridRendererConfig.
   */
  public getDefaultGridRendererConfig(): ClientGridRendererConfig {
    return {
      displayFields: DEFAULT_GRID_RENDERER_CONFIG.displayFields.map(f => ({...f})),
      detailFields: DEFAULT_GRID_RENDERER_CONFIG.detailFields.map(f => ({...f}))
    };
  }

  /**
   * Gets the effective grid renderer config (custom or default).
   *
   * @param customConfig The custom config from API or null.
   * @returns The effective config to use.
   */
  public getEffectiveGridRendererConfig(customConfig: ClientGridRendererConfig | null): ClientGridRendererConfig {
    if (customConfig && (customConfig.displayFields.length > 0 || customConfig.detailFields.length > 0)) {
      return customConfig;
    }
    return this.getDefaultGridRendererConfig();
  }

  // =========================================================================
  // Private API Helpers
  // =========================================================================

  /**
   * Gets a setting value from the API.
   *
   * @param key The setting key.
   * @returns Promise resolving to the value or null if not found.
   */
  private getSetting<T>(key: string): Promise<T | null> {
    const basePath = this.getSettingsApiUrl('/get');

    return new Promise((resolve) => {
      this.http.post(basePath, {key}, {headers: this.getAuthHeaders()})
        .subscribe(
          (response: any) => {
            if (response && response.value !== undefined && response.value !== null && response.value !== '') {
              try {
                const parsed = typeof response.value === 'string' ? JSON.parse(response.value) : response.value;
                resolve(parsed as T);
              } catch (e) {
                resolve(response.value as T);
              }
            } else {
              resolve(null);
            }
          },
          error => {
            console.error(`Failed to get setting ${key}`, error);
            resolve(null);
          }
        );
    });
  }

  /**
   * Sets a setting value via the API.
   *
   * @param key The setting key.
   * @param value The value to set.
   */
  private setSetting<T>(key: string, value: T): Promise<void> {
    const basePath = this.getSettingsApiUrl();
    const serializedValue = typeof value === 'string' ? value : JSON.stringify(value);

    return new Promise((resolve, reject) => {
      this.http.post(basePath, {key, value: serializedValue}, {headers: this.getAuthHeaders()})
        .subscribe(
          () => resolve(),
          error => {
            console.error(`Failed to set setting ${key}`, error);
            reject(error);
          }
        );
    });
  }

  /**
   * Returns the API URL for settings.
   *
   * @param suffix Optional path suffix.
   */
  private getSettingsApiUrl(suffix: string = ''): string {
    const uri = this.configService.get(CONFIG_KEYS.SETTINGS_URI);
    return `${uri}${suffix}`;
  }

  /**
   * Returns auth headers by auth token and tenant id.
   */
  private getAuthHeaders(): any {
    const headers: any = {};
    const authToken = this.registryService.get(CONFIG_KEYS.SESSION_AUTH_TOKEN);
    const tenantId = this.registryService.get(CONFIG_KEYS.SESSION_TENANT_ID);

    if (authToken) {
      headers['X-Api-Token'] = authToken;
    }

    if (tenantId) {
      headers['X-Api-TenantId'] = `${tenantId}`;
    }

    return headers;
  }
}
